import React, { useState, useRef, useEffect, useCallback } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { FaGithub, FaLinkedin, FaInstagram } from "react-icons/fa";
import { SiGmail } from "react-icons/si";
import Particle from "../Particle";
import ReCAPTCHA from "react-google-recaptcha";
import DOMPurify from "dompurify";
import Modal from "../Modal/Modal";

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [rateLimitError, setRateLimitError] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const recaptchaRef = useRef(null);

  // Rate limiting settings
  const RATE_LIMIT_WINDOW = 5 * 60 * 1000; // 5 minutes in milliseconds
  const MAX_SUBMISSIONS = 3; // Maximum 3 submissions in 5 minutes

  const checkRateLimit = useCallback(() => {
    const rateLimitData = localStorage.getItem('contactFormRateLimit');
    if (rateLimitData) {
      const { count, timestamp } = JSON.parse(rateLimitData);
      const now = Date.now();
      
      if (now - timestamp < RATE_LIMIT_WINDOW) {
        if (count >= MAX_SUBMISSIONS) {
          const remainingTime = Math.ceil((RATE_LIMIT_WINDOW - (now - timestamp)) / 1000 / 60);
          setRateLimitError(`You have reached the limit of ${MAX_SUBMISSIONS} messages in 5 minutes. Please wait ${remainingTime} minutes.`);
          return true;
        }
      } else {
        localStorage.removeItem('contactFormRateLimit');
      }
    }
    setRateLimitError(null);
    return false;
  }, [RATE_LIMIT_WINDOW, MAX_SUBMISSIONS]);

  useEffect(() => {
    checkRateLimit();
  }, [checkRateLimit]);

  const updateRateLimit = () => {
    const rateLimitData = localStorage.getItem('contactFormRateLimit');
    const now = Date.now();
    
    if (rateLimitData) {
      const { count, timestamp } = JSON.parse(rateLimitData);
      if (now - timestamp < RATE_LIMIT_WINDOW) {
        localStorage.setItem('contactFormRateLimit', JSON.stringify({
          count: count + 1,
          timestamp
        }));
      } else {
        localStorage.setItem('contactFormRateLimit', JSON.stringify({
          count: 1,
          timestamp: now
        }));
      }
    } else {
      localStorage.setItem('contactFormRateLimit', JSON.stringify({
        count: 1,
        timestamp: now
      }));
    }
  };

  const sanitizeInput = (input) => {
    const sanitized = DOMPurify.sanitize(input, { ALLOWED_TAGS: [] });
    return sanitized
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!recaptchaValue) {
      setError("Please complete the reCAPTCHA verification.");
      return;
    }

    if (checkRateLimit()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const sanitizedData = {
        name: sanitizeInput(formData.name),
        email: sanitizeInput(formData.email),
        subject: sanitizeInput(formData.subject),
        message: sanitizeInput(formData.message)
      };

      const response = await fetch(`https://formspree.io/f/${process.env.REACT_APP_FORMSPREE_ID}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...sanitizedData,
          _replyto: sanitizedData.email,
          _subject: `New Contact Form Submission from ${sanitizedData.name}`,
          'g-recaptcha-response': recaptchaValue
        }),
      });

      if (response.ok) {
        updateRateLimit();
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
        });
        recaptchaRef.current.reset();
        setRecaptchaValue(null);
        setShowSuccessModal(true);
      } else {
        setError("Failed to send message. Please try again.");
      }
    } catch (err) {
      setError("An error occurred. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const onRecaptchaChange = (value) => {
    setRecaptchaValue(value);
  };

  return (
    <Container fluid className="contact-section">
      <Particle />
      <Container>
        <Row style={{ justifyContent: "center", padding: "10px" }}>
          <Col
            md={7}
            style={{
              justifyContent: "center",
              paddingTop: "30px",
              paddingBottom: "50px",
            }}
          >
            <h1 style={{ fontSize: "2.1em", paddingBottom: "20px" }}>
              Get in <strong className="purple">Touch</strong>
            </h1>
            {error && <div className="alert alert-danger">{error}</div>}
            {rateLimitError && <div className="alert alert-warning">{rateLimitError}</div>}
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  maxLength={30}
                  pattern="[A-Za-z0-9\s\-']+"
                  title="Please enter only letters, numbers, spaces, hyphens, and apostrophes"
                />
              </div>
              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  maxLength={50}
                  pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                  title="Please enter a valid email address"
                />
              </div>
              <div className="form-group">
                <input
                  type="text"
                  name="subject"
                  className="form-control"
                  placeholder="Subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  maxLength={100}
                />
              </div>
              <div className="form-group">
                <textarea
                  name="message"
                  className="form-control"
                  rows="5"
                  placeholder="Your Message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  maxLength={1000}
                ></textarea>
              </div>
              <div className="form-group" style={{ marginBottom: "20px" }}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={onRecaptchaChange}
                  theme="dark"
                />
              </div>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={isSubmitting || !recaptchaValue || !!rateLimitError}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </Col>
          <Col
            md={5}
            style={{ paddingTop: "120px", paddingBottom: "50px" }}
            className="about-img"
          >
            <div className="contact-social-icons">
              <h1>Follow <strong className="purple">Me</strong></h1>
              <p>
                Feel free to connect with me on my social media accounts.
              </p>
              <ul className="footer-icons">
                <li className="social-icons">
                  <a
                    href="https://github.com/uurkrtl"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="GitHub"
                  >
                    <FaGithub />
                  </a>
                </li>
                <li className="social-icons">
                  <a
                    href="https://x.com/uurkrtl"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Twitter"
                  >
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                </li>
                <li className="social-icons">
                  <a
                    href="https://www.linkedin.com/in/ugurkartal"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                  >
                    <FaLinkedin />
                  </a>
                </li>
                <li className="social-icons">
                  <a
                    href="https://www.instagram.com/uurkrtl"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                  >
                    <FaInstagram />
                  </a>
                </li>
                <li className="social-icons">
                  <a
                    href="mailto:kartalugur@gmail.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Email"
                  >
                    <SiGmail />
                  </a>
                </li>
              </ul>
            </div>
          </Col>
        </Row>
      </Container>

      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Message Sent Successfully!"
        message="Thank you for your message. I will get back to you as soon as possible."
      />
    </Container>
  );
}

export default Contact; 